/**
 * RoleEditor — selettore ruolo/flussi intelligente.
 *
 * La UI distingue due livelli:
 *   1. Ruolo base: Admin | Operatore | Editor | Viewer  (radio)
 *   2. Flussi:     solo se "Operatore" e solo quelli del cliente scelto (toggle pill)
 *
 * Tenant speciali (nessun modulo specifico = tutti i flussi):
 *   - platform_admin → nessun cliente, mostra solo il ruolo base
 *
 * Mapping modulo → ruolo Zitadel:
 *   rdi      → "operator"
 *   ddt      → "ddt_operator"
 *   bsc      → "bsc_operator"
 *   rinascente     → "rinascente_operator"
 *   deleghe  → "delega_operator"
 */

import type { AdminTenant } from "../api/types";

// ── Tipi ────────────────────────────────────────────────────

export type BaseRole = "platform_admin" | "admin" | "operator" | "editor" | "viewer";

export interface RoleEditorValue {
  base: BaseRole;
  flows: string[];  // moduli selezionati (es. ["rdi", "ddt"])
}

// ── Mapping modulo → ruolo Zitadel ──────────────────────────

export const MODULE_ROLE: Record<string, string> = {
  rdi:     "operator",
  ddt:     "ddt_operator",
  bsc:     "bsc_operator",
  rinascente:    "rinascente_operator",
  deleghe: "delega_operator",
};

export const MODULE_LABEL: Record<string, { label: string; icon: string; desc: string }> = {
  rdi:     { label: "RDI",     icon: "assignment",    desc: "Richieste di intervento" },
  ddt:     { label: "DDT",     icon: "local_shipping", desc: "Documenti di trasporto" },
  bsc:     { label: "BSC",     icon: "description",   desc: "Bill of Sale Certificate" },
  rinascente:    { label: "Rinascente", icon: "receipt_long",  desc: "Fatture estere" },
  deleghe: { label: "Deleghe", icon: "verified_user", desc: "Poteri di firma" },
};

const BASE_ROLES: { value: BaseRole; label: string; desc: string }[] = [
  { value: "admin",          label: "Admin",    desc: "Gestione completa del tenant" },
  { value: "operator",       label: "Operatore",desc: "Lavora sui flussi selezionati" },
  { value: "editor",         label: "Editor",   desc: "Coda output RDI" },
  { value: "viewer",         label: "Viewer",   desc: "Sola lettura" },
];

const BASE_ROLES_WITH_SUPER: typeof BASE_ROLES = [
  { value: "platform_admin", label: "Platform Admin", desc: "Accesso a tutti i tenant" },
  ...BASE_ROLES,
];

// ── Conversione ruoli Zitadel ↔ RoleEditorValue ─────────────

function parse(zitadelRoles: string[]): RoleEditorValue {
  if (zitadelRoles.includes("platform_admin")) return { base: "platform_admin", flows: [] };
  if (zitadelRoles.includes("admin"))          return { base: "admin",          flows: [] };
  if (zitadelRoles.includes("editor"))         return { base: "editor",         flows: [] };
  if (zitadelRoles.includes("viewer"))         return { base: "viewer",         flows: [] };

  // È un operatore — troviamo i flussi dalla lista dei ruoli
  const flows: string[] = [];
  for (const [mod, role] of Object.entries(MODULE_ROLE)) {
    if (zitadelRoles.includes(role)) flows.push(mod);
  }
  return { base: "operator", flows };
}

function toZitadelRoles(v: RoleEditorValue): string[] {
  if (v.base !== "operator") return [v.base];
  if (v.flows.length === 0) return ["operator"]; // fallback
  return v.flows.map((m) => MODULE_ROLE[m] ?? m);
}

/** Funzione + namespace per parse — import singolo */
export const rolesFromEditor = Object.assign(toZitadelRoles, { parse });

// ── Label per la visualizzazione (senza editing) ─────────────

export function RoleDisplayBadges({
  roles,
  tenantModules,
}: {
  roles: string[];
  tenantModules: string[];
}) {
  const v = parse(roles);

  if (v.base === "platform_admin") return <span className="badge badge-danger">Platform Admin</span>;
  if (v.base === "admin")   return <span className="badge badge-info">Admin</span>;
  if (v.base === "editor")  return <span className="badge badge-neutral">Editor</span>;
  if (v.base === "viewer")  return <span className="badge badge-neutral">Viewer</span>;

  // Operatore — mostra i flussi
  const activeFlows = v.flows.length > 0
    ? v.flows
    : tenantModules; // se ruoli vuoti ma ha moduli, mostra tutti come fallback

  if (activeFlows.length === 0) return <span className="badge badge-neutral">Operatore</span>;

  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      {activeFlows.map((m) => (
        <span key={m} className="badge badge-info" style={{ fontSize: "var(--font-size-xs)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 12, marginRight: 3, verticalAlign: "middle" }}>
            {MODULE_LABEL[m]?.icon ?? "circle"}
          </span>
          {MODULE_LABEL[m]?.label ?? m}
        </span>
      ))}
    </span>
  );
}

// ── RoleEditor component ─────────────────────────────────────

export function RoleEditor({
  value,
  onChange,
  tenants,
  currentTenantId,
  lockTenant = false,
  isPlatformAdmin = false,
}: {
  value: RoleEditorValue;
  onChange: (v: RoleEditorValue) => void;
  tenants: AdminTenant[];
  currentTenantId: string;
  lockTenant?: boolean;       // se true, non mostrare il cambio cliente
  isPlatformAdmin?: boolean;  // se true, mostra la voce Platform Admin
}) {
  const tenant = tenants.find((t) => t.tenant_id === currentTenantId);
  const modules = tenant?.modules ?? [];

  // Se un tenant ha un solo modulo, l'operatore accede per forza a quel flusso
  const singleModule = modules.length === 1 ? modules[0] : null;

  const setBase = (b: BaseRole) => {
    const flows = b === "operator" && singleModule ? [singleModule] : [];
    onChange({ base: b, flows });
  };

  const toggleFlow = (mod: string) => {
    const current = value.flows;
    const next = current.includes(mod)
      ? current.filter((m) => m !== mod)
      : [...current, mod];
    onChange({ ...value, flows: next });
  };

  const baseOptions = isPlatformAdmin ? BASE_ROLES_WITH_SUPER : BASE_ROLES;

  return (
    <div>
      {/* Ruolo base */}
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", marginBottom: "var(--space-3)" }}>
        {baseOptions.map((r) => {
          const active = value.base === r.value;
          return (
            <button
              key={r.value}
              type="button"
              onClick={() => setBase(r.value)}
              className={`btn-toggle-sm${active ? " active" : ""}`}
              style={{ flexDirection: "column", height: "auto", alignItems: "flex-start", gap: 2, padding: "var(--space-2) var(--space-3)" }}
            >
              <div style={{ fontSize: "var(--font-size-sm)", fontWeight: 600 }}>
                {r.label}
              </div>
              <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)" }}>{r.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Flussi — solo per Operatore con cliente che ha più moduli */}
      {value.base === "operator" && modules.length > 0 && !singleModule && (
        <div>
          <div style={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "var(--indaco-gray-500)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: "var(--space-2)" }}>
            Flussi abilitati
          </div>
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            {modules.map((mod) => {
              const meta = MODULE_LABEL[mod] ?? { label: mod, icon: "circle", desc: "" };
              const active = value.flows.includes(mod);
              return (
                <button
                  key={mod}
                  type="button"
                  onClick={() => toggleFlow(mod)}
                  className={`btn-toggle-sm${active ? " active" : ""}`}
                  style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                    {meta.icon}
                  </span>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "var(--font-size-sm)", fontWeight: 600 }}>
                      {meta.label}
                    </div>
                    <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)" }}>{meta.desc}</div>
                  </div>
                  {active && (
                    <span className="material-symbols-outlined" style={{ fontSize: 16, marginLeft: 2 }}>check</span>
                  )}
                </button>
              );
            })}
          </div>
          {value.flows.length === 0 && (
            <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-warning)", marginTop: "var(--space-1)" }}>
              Seleziona almeno un flusso
            </div>
          )}
        </div>
      )}

      {/* Tenant con un solo modulo: messaggio esplicativo */}
      {value.base === "operator" && singleModule && (
        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)", display: "flex", alignItems: "center", gap: 6 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>info</span>
          Accesso automatico al flusso <strong>{MODULE_LABEL[singleModule]?.label ?? singleModule}</strong>
        </div>
      )}
    </div>
  );
}
